import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaEdit, FaTrash, FaEye, FaBox } from 'react-icons/fa';
import Swal from 'sweetalert2';
import PackageCreate from '../../create/js/packageCreate';
import DataTable from '../../../../components_shared/DataTable';
import TableToolbar from '../../../../components_shared/TableToolbar';
import { packageAPI } from '../../../../utils/api';
import PropTypes from 'prop-types';

function PackageTable({ onCreatePackage, reloadKey = 0 }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [editModal, setEditModal] = useState(false);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const res = await packageAPI.getAll();
      setData(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e?.response?.data?.error || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPackages(); }, [reloadKey]);

  const remove = async (pkg) => {
    const ok = await Swal.fire({
      title: 'Delete Package?',
      text: `"${pkg.name}" will be permanently removed.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#e74c3c',
    });
    if (ok.isConfirmed) {
      try {
        await packageAPI.delete(pkg.id);
        await fetchPackages();
        Swal.fire({ icon: 'success', title: 'Deleted', toast: true, position: 'top-end', timer: 3000, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: 'Error', text: e?.response?.data?.error || e.message });
      }
    }
  };

  const exportCsv = async () => {
    try {
      const res = await packageAPI.exportCsv();
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'packages_export.csv'; a.click(); URL.revokeObjectURL(url);
    } catch (err) { Swal.fire({ icon: 'error', title: 'Export failed', text: err.message }); }
  };

  const importCsv = async (csv) => {
    const res = await packageAPI.importCsv(csv);
    await fetchPackages();
    return res.data;
  };

  const downloadSample = async () => {
    try {
      const res = await packageAPI.sampleCsv();
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'packages_sample.csv'; a.click(); URL.revokeObjectURL(url);
    } catch (err) { console.error(err); }
  };

  const columns = [
    { accessorKey: 'id', header: 'ID', size: 60 },
    {
      accessorKey: 'package_ref',
      header: 'Reference',
      cell: ({ getValue }) => <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#64748b' }}>{getValue()}</span>,
    },
    {
      accessorKey: 'name',
      header: 'Package Name',
      cell: ({ getValue }) => <span style={{ fontWeight: 600, color: '#2d3436' }}>{getValue()}</span>,
    },
    { accessorKey: 'project_name', header: 'Project' },
    { accessorKey: 'class_name',   header: 'Class' },
    {
      accessorKey: 'book_names',
      header: 'Books',
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem', color: '#636e72' }}>{getValue() || '-'}</span>,
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ getValue }) => (
        <span className={`eduss-global-status-badge ${getValue() ? 'active' : 'inactive'}`}>
          {getValue() ? 'ACTIVE' : 'INACTIVE'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="eduss-global-action-buttons">
          <button className="eduss-global-action-btn view"   onClick={() => { setSelected(row.original); setViewModal(true); }}><FaEye /></button>
          <button className="eduss-global-action-btn edit"   onClick={() => { setSelected(row.original); setEditModal(true); }}><FaEdit /></button>
          <button className="eduss-global-action-btn delete" onClick={() => remove(row.original)}><FaTrash /></button>
        </div>
      ),
    },
  ];

  const filteredData = data.filter(p =>
    !filter ||
    p.name?.toLowerCase().includes(filter.toLowerCase()) ||
    p.package_ref?.toLowerCase().includes(filter.toLowerCase()) ||
    p.project_name?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="premium-card" style={{ padding: '0 0 30px 0' }}>
      <TableToolbar
        title="Package Management"
        subtitle="Bundle publications into distribution packages for institutions"
        onAdd={onCreatePackage}
        onExport={exportCsv}
        onImport={importCsv}
        onDownloadSample={downloadSample}
        onSearch={setFilter}
      />

      <div style={{ padding: '0 30px' }}>
        <DataTable
          data={filteredData}
          columns={columns}
          loading={loading}
          enableSelection={true}
        />
      </div>

      {viewModal && selected && createPortal(
        <div className="eduss-global-modal-overlay" onClick={() => setViewModal(false)}>
          <div className="eduss-global-modal eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Package Details</h3>
              <button onClick={() => setViewModal(false)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <div style={{ display: 'flex', gap: 20, marginBottom: 24, alignItems: 'center' }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(15,111,173,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.75rem', color: '#0f6fad', flexShrink: 0 }}>
                  <FaBox />
                </div>
                <div>
                  <h4 style={{ margin: '0 0 4px', fontSize: '1.2rem', color: '#1e293b' }}>{selected.name}</h4>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>Ref: {selected.package_ref}</p>
                </div>
              </div>
              <table className="mini-info-table">
                <tbody>
                  <tr><th>Project</th><td>{selected.project_name || '-'}</td></tr>
                  <tr><th>Class</th><td>{selected.class_name || '-'}</td></tr>
                  <tr><th>Books Included</th><td>{selected.book_names || '-'}</td></tr>
                  <tr><th>Status</th><td>
                    <span className={`eduss-global-status-badge ${selected.is_active ? 'active' : 'inactive'}`}>
                      {selected.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>,
        document.body
      )}

      {editModal && selected && createPortal(
        <div className="eduss-global-modal-overlay" onClick={() => setEditModal(false)}>
          <div className="eduss-global-modal large eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Update Package</h3>
              <button onClick={() => setEditModal(false)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <PackageCreate
                key={selected?.id}
                packageData={selected}
                onClose={() => setEditModal(false)}
                onSubmit={() => { fetchPackages(); setEditModal(false); }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

PackageTable.propTypes = {
  onCreatePackage: PropTypes.func,
  reloadKey: PropTypes.number,
};
export default PackageTable;
